import React from 'react';
import {
  VStack,
  Text,
  Box,
  HStack,
  Button
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { ContentSubmissionUnit, ContentRequirement } from '@foodfluence/shared';
import { getAllContentSlots } from '../../../utils/groupedContentUtils';
import { ff } from '../../../theme';
import { ContentPreview } from './ContentPreview';
import { ContentUploadArea } from './ContentUploadArea';
import { 
  getAcceptedFileTypes, 
  formatFileSize, 
  isImageFile, 
  isVideoFile 
} from '../../../utils/contentUploadUtils';

interface ContentUploadSectionProps {
  contentRequirement?: ContentRequirement;
  contentMap: Record<string, ContentSubmissionUnit>;
  contentId: string;
  uploadedFiles: Record<string, File>;
  contentUrls: Record<string, string>;
  isLoadingContent: Record<string, boolean>;
  onFileUpload: (contentKey: string) => void;
  onFileRemove: (contentKey: string) => void;
  onDrop: (acceptedFiles: File[], contentKey: string) => void;
}

export const ContentUploadSection: React.FC<ContentUploadSectionProps> = ({
  contentRequirement,
  contentMap,
  contentId,
  uploadedFiles,
  contentUrls,
  isLoadingContent,
  onFileUpload,
  onFileRemove,
  onDrop,
}) => {
  const isCarousel = contentRequirement && (contentRequirement.count || 1) > 1;
  const requiredCount = contentRequirement ? (contentRequirement.count || 1) : 1;

  // Get all content slots (existing + empty) for this requirement
  const itemsToRender = getAllContentSlots(contentMap, contentRequirement, contentId);

  const { getRootProps, getInputProps, isDragActive: dropzoneDragActive } = useDropzone({
    onDrop: (files) => onDrop(files, contentId),
    accept: getAcceptedFileTypes(contentRequirement),
    maxFiles: 1,
    multiple: false,
    useFsAccessApi: false,
  });

  const renderUploadItem = (contentKey: string, existingContent: ContentSubmissionUnit | undefined, index: number) => {
    const file = uploadedFiles[contentKey];
    
    // Show preview if:
    // 1. There's a new uploaded file, OR
    // 2. There's existing content with a fileUrl (even if pending submission)
    const hasContent = file || (existingContent?.fileUrl);
    
    return (
      <Box key={contentKey}>
        {isCarousel && (
          <Text fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2} mb="8px">
            Item {index + 1}
          </Text>
        )}
        {hasContent ? (
          // Content Preview - already has its own styling
            <ContentPreview
              contentKey={contentKey}
              index={index}
              contentType={
                file ? (isImageFile(file) ? 'image' : isVideoFile(file) ? 'video' : 'none') :
                existingContent?.fileUrl?.match(/\.(mp4|mov|avi|wmv)$/i) ? 'video' : 'image'
              }
              previewUrl={file ? URL.createObjectURL(file) : contentUrls[contentKey]}
              fileSize={file?.size}
              status={existingContent?.status}
              feedback={existingContent?.feedback}
              isLoadingContent={isLoadingContent[contentKey]}
              onRemove={onFileRemove}
              onReplace={onFileUpload}
              isCarousel={isCarousel}
            />
        ) : (
          // Upload Area - ContentUploadArea has its own styling
          <Box
            position="relative"
          >
            <ContentUploadArea
              contentRequirement={contentRequirement}
              isDragActive={dropzoneDragActive}
              onClick={() => onFileUpload(contentKey)}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <VStack spacing="16px" align="stretch">
      {isCarousel && (
        <Text fontSize="16px" fontWeight={600} color={ff.ink} lineHeight="normal">
          Upload {requiredCount} {contentRequirement?.type}(s) for carousel
        </Text>
      )}
      {itemsToRender.map(({ key: contentKey, unit: existingContent }, index) => 
        renderUploadItem(contentKey, existingContent, index)
      )}
    </VStack>
  );
};
